import { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import ReviewCard from '../components/ReviewCard';
import { trackEvent } from '../services/analytics';
import SubscriptionGate from '../components/SubscriptionGate';

const TABS = [
  { key: 'all', label: 'All' },
  { key: 'pending', label: 'Pending' },
  { key: 'generated', label: 'Generated' },
  { key: 'approved', label: 'Approved' },
  { key: 'posted', label: 'Posted' },
  { key: 'skipped', label: 'Skipped' },
];

const PLATFORMS_BY_CATEGORY = {
  local_business: [
    { key: 'google', label: 'Google', icon: '🔍' },
    { key: 'trustpilot', label: 'Trustpilot', icon: '⭐' },
  ],
  hospitality: [
    { key: 'airbnb', label: 'Airbnb', icon: '🏠' },
  ],
};

/* ─── Bulk Import Modal ─── */
function BulkImportModal({ onClose, onImported, businessCategory }) {
  const [pastedText, setPastedText] = useState('');
  const [parsing, setParsing] = useState(false);
  const [parsedReviews, setParsedReviews] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(0);
  const [errors, setErrors] = useState([]);
  const [selectedReviews, setSelectedReviews] = useState({});

  const platform = businessCategory === 'hospitality' ? 'airbnb' : 'google';

  const handleParse = async () => {
    if (!pastedText.trim() || pastedText.trim().length < 20) return;
    setParsing(true);
    setErrors([]);
    try {
      const result = await api.post('/bulk-import/parse', {
        pasted_text: pastedText.trim(),
        platform,
      });
      setParsedReviews(result.reviews || []);
      const selected = {};
      (result.reviews || []).forEach((_, i) => { selected[i] = true; });
      setSelectedReviews(selected);
      trackEvent('bulk_parse_completed', { count: result.count });
    } catch (err) {
      setErrors([err.message || 'Failed to parse reviews']);
    } finally {
      setParsing(false);
    }
  };

  const toggleReview = (idx) => {
    setSelectedReviews(prev => ({ ...prev, [idx]: !prev[idx] }));
  };

  const selectedCount = Object.values(selectedReviews).filter(Boolean).length;

  const normalizeDate = (dateStr) => {
    if (!dateStr) return new Date().toISOString().split('T')[0];
    if (/^\d{4}-\d{2}$/.test(dateStr)) return `${dateStr}-01`;
    if (/^\d{4}$/.test(dateStr)) return `${dateStr}-01-01`;
    if (/^\d{4}-\d{2}-\d{2}/.test(dateStr)) return dateStr.slice(0, 10);
    return new Date().toISOString().split('T')[0];
  };

  const handleSubmitAll = async () => {
    setSubmitting(true);
    setErrors([]);
    let success = 0;
    const newErrors = [];

    for (let i = 0; i < parsedReviews.length; i++) {
      if (!selectedReviews[i]) continue;
      const r = parsedReviews[i];
      try {
        await api.addManualReview(r.reviewer_name, r.rating, r.review_text, platform, normalizeDate(r.review_date), false);
        success++;
        setSubmitted(success);
      } catch (err) {
        newErrors.push(`${r.reviewer_name}: ${err.message}`);
      }
    }

    if (newErrors.length > 0) setErrors(newErrors);
    trackEvent('bulk_import_completed', { imported: success, errors: newErrors.length });
    if (success > 0) onImported(success);
  };

  const isHospitality = businessCategory === 'hospitality';

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content card" onClick={e => e.stopPropagation()} style={{ maxWidth: 640, maxHeight: '90vh', overflow: 'auto' }}>
        <div className="modal-header">
          <h2 className="modal-title">Bulk Import Reviews</h2>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>

        {!parsedReviews ? (
          <div>
            <p style={{ fontSize: 13, color: 'var(--text-light)', marginBottom: 16, lineHeight: 1.6 }}>
              {isHospitality ? (
                <>Go to your <strong>Airbnb hosting dashboard</strong>, open your listing's reviews, select all the reviews you want to import, copy the text, and paste it below. AI will automatically detect and separate each review.</>
              ) : (
                <>Copy reviews from Google Maps or Trustpilot and paste them below. AI will automatically detect and separate each review.</>
              )}
            </p>
            <div style={{ padding: '12px 16px', background: 'var(--bg)', borderRadius: 8, marginBottom: 16, fontSize: 12, color: 'var(--text-light)', lineHeight: 1.7 }}>
              <strong style={{ color: 'var(--text)', fontSize: 12 }}>{isHospitality ? 'How to copy from Airbnb:' : 'How to copy reviews:'}</strong><br />
              {isHospitality ? (
                <>1. Open <a href="https://www.airbnb.co.uk/hosting/reviews" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent)' }}>airbnb.co.uk/hosting/reviews</a><br />2. Select the reviews (Ctrl+A or Cmd+A)<br />3. Copy (Ctrl+C or Cmd+C)<br />4. Paste below</>
              ) : (
                <>1. Open your Google Maps listing and go to Reviews<br />2. Select and copy the reviews you want to import<br />3. Paste below</>
              )}
            </div>
            <textarea value={pastedText} onChange={e => setPastedText(e.target.value)} placeholder={isHospitality ? "Paste your Airbnb reviews here..." : "Paste your reviews here..."} rows={12} style={{ width: '100%', padding: '14px 16px', border: '1px solid var(--border)', borderRadius: 8, fontSize: 13, fontFamily: 'inherit', resize: 'vertical', lineHeight: 1.6, background: '#fff' }} />
            <div style={{ fontSize: 11, color: 'var(--text-light)', marginTop: 6 }}>{pastedText.length > 0 ? `${pastedText.length.toLocaleString()} characters pasted` : 'Paste your reviews above'}</div>
            {errors.length > 0 && <div style={{ marginTop: 12 }}>{errors.map((e, i) => <div key={i} className="form-error" style={{ marginBottom: 4 }}>{e}</div>)}</div>}
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 16 }}>
              <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
              <button className="btn btn-primary" onClick={handleParse} disabled={parsing || pastedText.trim().length < 20}>{parsing ? 'Detecting reviews...' : '🔍 Detect Reviews'}</button>
            </div>
          </div>
        ) : submitting || submitted > 0 ? (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            {submitted > 0 && !submitting ? (
              <>
                <div style={{ fontSize: 40, marginBottom: 12 }}>✅</div>
                <h3 style={{ fontSize: 18, margin: '0 0 8px' }}>{submitted} review{submitted !== 1 ? 's' : ''} imported!</h3>
                <p style={{ fontSize: 13, color: 'var(--text-light)', marginBottom: 16 }}>Click Generate on any review to create a response.</p>
                {errors.length > 0 && <div style={{ textAlign: 'left', marginBottom: 16 }}><p style={{ fontSize: 12, color: '#dc2626', fontWeight: 600 }}>{errors.length} failed:</p>{errors.map((e, i) => <div key={i} style={{ fontSize: 11, color: '#dc2626', marginTop: 4 }}>{e}</div>)}</div>}
                <button className="btn btn-primary" onClick={onClose}>Done</button>
              </>
            ) : (
              <>
                <div style={{ fontSize: 14, color: 'var(--text)', marginBottom: 8 }}>Importing reviews... {submitted}/{selectedCount}</div>
                <div style={{ width: '100%', height: 6, background: 'var(--bg)', borderRadius: 3, overflow: 'hidden', marginBottom: 8 }}><div style={{ width: `${(submitted / selectedCount) * 100}%`, height: '100%', background: 'var(--accent)', borderRadius: 3, transition: 'width 0.3s ease' }} /></div>
                <p style={{ fontSize: 12, color: 'var(--text-light)' }}>Saving your reviews, this may take a moment...</p>
              </>
            )}
          </div>
        ) : (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
              <p style={{ fontSize: 13, color: 'var(--text-light)', margin: 0 }}>Found <strong style={{ color: 'var(--text)' }}>{parsedReviews.length}</strong> review{parsedReviews.length !== 1 ? 's' : ''}. Uncheck any you don't want to import.</p>
              <button className="btn btn-ghost btn-sm" onClick={() => { setParsedReviews(null); setSelectedReviews({}); }} style={{ fontSize: 11 }}>← Back to paste</button>
            </div>
            <div style={{ maxHeight: 400, overflow: 'auto', marginBottom: 16 }}>
              {parsedReviews.map((r, i) => (
                <div key={i} onClick={() => toggleReview(i)} style={{ display: 'flex', gap: 12, padding: '12px 14px', borderRadius: 8, marginBottom: 8, cursor: 'pointer', background: selectedReviews[i] ? 'rgba(52,211,153,0.04)' : 'var(--bg)', border: `1px solid ${selectedReviews[i] ? 'rgba(52,211,153,0.2)' : 'var(--border)'}`, opacity: selectedReviews[i] ? 1 : 0.5, transition: 'all 0.15s ease' }}>
                  <div style={{ width: 20, height: 20, borderRadius: 4, flexShrink: 0, marginTop: 2, border: `2px solid ${selectedReviews[i] ? 'var(--accent)' : 'var(--border)'}`, background: selectedReviews[i] ? 'var(--accent)' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.15s ease' }}>
                    {selectedReviews[i] && <span style={{ color: '#fff', fontSize: 12, fontWeight: 700 }}>✓</span>}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                      <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{r.reviewer_name}</span>
                      <span style={{ fontSize: 12, color: '#f59e0b' }}>{'★'.repeat(r.rating)}{'☆'.repeat(5 - r.rating)}</span>
                      {r.review_date && <span style={{ fontSize: 11, color: 'var(--text-light)', marginLeft: 'auto' }}>{r.review_date}</span>}
                    </div>
                    <p style={{ fontSize: 12, color: 'var(--text-light)', margin: 0, lineHeight: 1.5, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical' }}>{r.review_text}</p>
                  </div>
                </div>
              ))}
            </div>
            {parsedReviews.length === 0 && <div style={{ textAlign: 'center', padding: '24px', color: 'var(--text-light)' }}><p style={{ fontSize: 14 }}>No reviews detected. Please go back and try pasting again.</p></div>}
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSubmitAll} disabled={selectedCount === 0}>Import {selectedCount} review{selectedCount !== 1 ? 's' : ''}</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ─── Add Review Modal ─── */
function AddReviewModal({ onClose, onSubmit, businessCategory }) {
  const platforms = PLATFORMS_BY_CATEGORY[businessCategory] || PLATFORMS_BY_CATEGORY.local_business;
  const [reviewerName, setReviewerName] = useState('');
  const [rating, setRating] = useState(5);
  const [reviewText, setReviewText] = useState('');
  const [platform, setPlatform] = useState(platforms[0].key);
  const [submitting, setSubmitting] = useState(false);
  const [hoverRating, setHoverRating] = useState(0);
  const [reviewDate, setReviewDate] = useState(new Date().toISOString().split('T')[0]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!reviewerName.trim()) return;
    setSubmitting(true);
    try { await onSubmit(reviewerName.trim(), rating, reviewText.trim() || null, platform, reviewDate); onClose(); }
    catch (err) { alert('Failed to add review: ' + err.message); }
    finally { setSubmitting(false); }
  };

  const isHospitality = businessCategory === 'hospitality';
  const platformHint = { google: 'Copy a review from Google Maps and paste it here.', trustpilot: 'Copy a review from your Trustpilot business page.', airbnb: 'Copy a guest review from your Airbnb dashboard.' };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content card" onClick={e => e.stopPropagation()}>
        <div className="modal-header"><h2 className="modal-title">Add Review</h2><button className="modal-close" onClick={onClose}>&times;</button></div>
        <p style={{ fontSize: '14px', color: 'var(--text-light)', marginBottom: '20px' }}>{platformHint[platform]} An AI response will be generated immediately.</p>
        <form onSubmit={handleSubmit}>
          {platforms.length > 1 && <div className="form-group"><label className="form-label">Platform</label><div className="platform-selector">{platforms.map(p => <button key={p.key} type="button" className={`platform-chip ${platform === p.key ? 'active' : ''}`} onClick={() => setPlatform(p.key)}>{p.icon} {p.label}</button>)}</div></div>}
          <div className="form-group"><label className="form-label">Review Date</label><input className="form-input" type="date" value={reviewDate} onChange={e => setReviewDate(e.target.value)} max={new Date().toISOString().split('T')[0]} /><span className="form-hint">When was this review originally posted?</span></div>
          <div className="form-group"><label className="form-label">{isHospitality ? 'Guest Name' : 'Reviewer Name'}</label><input className="form-input" type="text" value={reviewerName} onChange={e => setReviewerName(e.target.value)} placeholder={isHospitality ? 'e.g. Sarah' : 'e.g. John Smith'} required autoFocus /></div>
          <div className="form-group"><label className="form-label">Rating</label><div className="star-picker">{[1,2,3,4,5].map(i => <span key={i} className={`star-pick ${i <= (hoverRating || rating) ? 'active' : ''}`} onClick={() => setRating(i)} onMouseEnter={() => setHoverRating(i)} onMouseLeave={() => setHoverRating(0)}>★</span>)}<span className="star-label">{rating}/5</span></div></div>
          <div className="form-group"><label className="form-label">Review Text <span style={{ fontWeight: 'normal', color: 'var(--text-light)' }}>(optional)</span></label><textarea className="form-input" value={reviewText} onChange={e => setReviewText(e.target.value)} placeholder={isHospitality ? "Paste the guest's review text here..." : "Paste the customer's review text here..."} rows={4} style={{ resize: 'vertical' }} /></div>
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' }}><button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button><button type="submit" className="btn btn-primary" disabled={submitting || !reviewerName.trim()}>{submitting ? 'Adding & Generating...' : 'Add Review'}</button></div>
        </form>
      </div>
    </div>
  );
}

/* ─── Confirm Delete Modal ─── */
function ConfirmDeleteModal({ reviewerName, onConfirm, onCancel }) {
  const [deleting, setDeleting] = useState(false);
  const handleConfirm = async () => { setDeleting(true); try { await onConfirm(); } finally { setDeleting(false); } };

  return (
    <div className="modal-overlay" onClick={onCancel}>
      <div className="modal-content card" onClick={e => e.stopPropagation()} style={{ maxWidth: '420px' }}>
        <div className="modal-header"><h2 className="modal-title">Delete Review</h2><button className="modal-close" onClick={onCancel}>&times;</button></div>
        <p style={{ fontSize: '14px', color: 'var(--text-light)', margin: '0 0 8px' }}>Are you sure you want to permanently delete the review from <strong style={{ color: 'var(--text)' }}>{reviewerName}</strong>?</p>
        <p style={{ fontSize: '13px', color: '#dc2626', margin: '0 0 20px' }}>This action cannot be undone.</p>
        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}><button className="btn btn-ghost" onClick={onCancel} disabled={deleting}>Cancel</button><button className="btn" onClick={handleConfirm} disabled={deleting} style={{ background: '#dc2626', color: '#fff', border: 'none', opacity: deleting ? 0.7 : 1 }}>{deleting ? 'Deleting...' : 'Delete Review'}</button></div>
      </div>
    </div>
  );
}

/* ─── Main Page ─── */
export default function ReviewsPage() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [toast, setToast] = useState(null);
  const [user, setUser] = useState(null);
  const [business, setBusiness] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [translatingAll, setTranslatingAll] = useState(false);
  const [importing, setImporting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [insightsSince, setInsightsSince] = useState(null);

  const showToast = (message, isError = false) => { setToast({ message, isError }); setTimeout(() => setToast(null), 4000); };

  const fetchReviews = useCallback(async () => {
    try { const data = await api.getReviews(); setReviews(data); }
    catch (err) { showToast('Failed to load reviews', true); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    fetchReviews();
    api.getMe().then(setUser).catch(() => {});
    api.getBusinesses().then(businesses => { if (businesses.length > 0) setBusiness(businesses[0]); }).catch(() => {});
    api.get('/competitors/insights').then(data => {
      const active = (data.insights || []).filter(i => !i.dismissed);
      if (active.length > 0) {
        const earliest = active.reduce((min, i) => { const d = i.first_seen_at; return d && (!min || d < min) ? d : min; }, null);
        setInsightsSince(earliest);
      }
    }).catch(() => {});
  }, [fetchReviews]);

  const translateEnabled = business?.translate_enabled || false;
  const translateLang = business?.translate_language || '';
  const hasGoogleUrl = !!business?.google_review_url;
  const businessCategory = business?.business_category || 'local_business';
  const isHospitality = businessCategory === 'hospitality';
  const isSubscribed = ['active', 'trialing'].includes(user?.subscription_status);

  const untranslatedCount = translateEnabled ? reviews.filter(r => r.review_text && (!r.translated_review_text || r.translated_language !== translateLang)).length : 0;

  const handleTranslateAll = async () => { setTranslatingAll(true); try { const result = await api.translateAllReviews(); showToast(`Translated ${result.translated} reviews`); trackEvent('reviews_translated', { count: result.translated }); await fetchReviews(); } catch (err) { showToast('Translation failed: ' + err.message, true); } finally { setTranslatingAll(false); } };

  const handleImportGoogle = async () => { setImporting(true); try { const result = await api.post('/reviews/import-google', {}); showToast(result.message); trackEvent('reviews_synced', { source: 'google', imported: result.imported }); if (result.imported > 0) await fetchReviews(); } catch (err) { const detail = err.message || 'Import failed'; showToast(detail.includes('No Google review URL') ? 'Set up your Google review URL in Settings first' : 'Import failed: ' + detail, true); } finally { setImporting(false); } };

  const handleBulkImported = async (count) => { showToast(`${count} review${count !== 1 ? 's' : ''} imported. Click Generate on any review to create a response.`); await fetchReviews(); setShowBulkModal(false); };

  const handleAddReview = async (reviewerName, rating, reviewText, platform, reviewDate) => {
    const newReview = await api.addManualReview(reviewerName, rating, reviewText, platform, reviewDate);
    setReviews(prev => [newReview, ...prev]);
    trackEvent('review_added', { source: 'manual', platform, rating });
    showToast(translateEnabled && newReview.translated_review_text ? 'Review added — AI response generated & translated!' : 'Review added — AI response generated!');
  };

  const handleGenerate = async (reviewId) => { try { const updated = await api.generateResponse(reviewId); setReviews(prev => prev.map(r => r.id === reviewId ? updated : r)); trackEvent('response_generated', { reviewId }); showToast('Response generated!'); } catch (err) { showToast('Generation failed: ' + err.message, true); } };
  const handleRegenerate = async (reviewId) => { try { const updated = await api.regenerateResponse(reviewId); setReviews(prev => prev.map(r => r.id === reviewId ? updated : r)); trackEvent('response_regenerated', { reviewId }); showToast('Response regenerated!'); } catch (err) { showToast('Regeneration failed: ' + err.message, true); } };
  const handleApprove = async (reviewId, editedResponse) => { try { const updated = await api.approveResponse(reviewId, editedResponse); setReviews(prev => prev.map(r => r.id === reviewId ? updated : r)); trackEvent('response_approved', { reviewId }); showToast('Response approved!'); } catch (err) { showToast('Approval failed: ' + err.message, true); } };
  const handleSkip = async (reviewId) => { try { const updated = await api.skipReview(reviewId); setReviews(prev => prev.map(r => r.id === reviewId ? updated : r)); showToast('Review skipped'); } catch (err) { showToast('Skip failed: ' + err.message, true); } };
  const handleDelete = async (reviewId) => { try { await api.deleteReview(reviewId); setReviews(prev => prev.filter(r => r.id !== reviewId)); setDeleteTarget(null); showToast('Review deleted'); } catch (err) { showToast('Delete failed: ' + err.message, true); } };

  const filteredReviews = activeTab === 'all' ? reviews : reviews.filter(r => (r.status || '').toLowerCase() === activeTab);
  const getCounts = (key) => key === 'all' ? reviews.length : reviews.filter(r => (r.status || '').toLowerCase() === key).length;

  if (loading) return <div className="loading">Loading reviews...</div>;

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Reviews</h1>
          <p className="page-subtitle">{isHospitality ? 'Manage and respond to your guest reviews' : 'Manage and respond to your customer reviews'}</p>
        </div>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          {!isHospitality && (
            <SubscriptionGate isSubscribed={isSubscribed} feature="Syncing reviews from Google">
              <button className="btn btn-ghost" onClick={handleImportGoogle} disabled={importing} title={hasGoogleUrl ? 'Import latest reviews from Google' : 'Set up Google review URL in Settings first'} style={{ display: 'flex', alignItems: 'center', gap: 6, opacity: importing ? 0.7 : 1 }}>
                {importing ? <><span style={{ display: 'inline-block', animation: 'spin 1s linear infinite' }}>🔄</span> Importing...</> : <>🔍 Sync from Google</>}
              </button>
            </SubscriptionGate>
          )}
          {isHospitality && (
            <SubscriptionGate isSubscribed={isSubscribed} feature="Bulk importing reviews">
              <button className="btn btn-ghost" onClick={() => setShowBulkModal(true)} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>📋 Bulk Import</button>
            </SubscriptionGate>
          )}
          <SubscriptionGate isSubscribed={isSubscribed} feature="Adding reviews">
            <button className="btn btn-primary" onClick={() => setShowAddModal(true)}>+ Add Review</button>
          </SubscriptionGate>
        </div>
      </div>

      <div className="filter-tabs">
        {TABS.map(tab => (
          <button key={tab.key} className={`filter-tab ${activeTab === tab.key ? 'active' : ''}`} onClick={() => setActiveTab(tab.key)}>
            {tab.label}<span className="count">{getCounts(tab.key)}</span>
          </button>
        ))}
      </div>

      {translateEnabled && (
        <div className="translate-indicator">
          <span>🌐 Translations to {translateLang}</span>
          {untranslatedCount > 0 && <button className="btn btn-sm btn-outline" onClick={handleTranslateAll} disabled={translatingAll} style={{ marginLeft: '12px' }}>{translatingAll ? 'Translating...' : `Translate ${untranslatedCount} review${untranslatedCount !== 1 ? 's' : ''}`}</button>}
          {untranslatedCount === 0 && <span style={{ marginLeft: '8px', color: 'var(--green)', fontSize: '13px' }}>✓ All translated</span>}
        </div>
      )}

      {isSubscribed && user?.subscription_status === 'trialing' && (
        <div className="card" style={{ padding: '16px', marginBottom: '20px', background: '#eef6ff', borderColor: '#3b82f6' }}>
          <strong style={{ color: '#3b82f6' }}>🕐 Free trial — {Math.max(0, Math.ceil((new Date(new Date(user.created_at).getTime() + 14*24*60*60*1000) - new Date()) / (1000*60*60*24)))} days remaining</strong>
        </div>
      )}

      {!isSubscribed && user && (
        <div className="card" style={{ padding: '20px', marginBottom: '20px', background: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.2)' }}>
          <strong style={{ color: '#d97706' }}>⚡ Trial expired</strong>
          <p style={{ margin: '4px 0 0', fontSize: '14px', color: 'var(--text-light)' }}>Your reviews and AI responses are saved. Subscribe to generate new responses and add reviews.</p>
          <a href="/billing" className="btn btn-primary" style={{ marginTop: '12px' }}>Subscribe Now</a>
        </div>
      )}

      <div className="review-list">
        {filteredReviews.length === 0 ? (
          <div className="empty-state card">
            <div className="empty-state-icon">💬</div>
            <div className="empty-state-text">
              {activeTab === 'all'
                ? (isHospitality ? 'No reviews yet. Click "Add Review" or "Bulk Import" to get started.' : 'No reviews yet. Click "Add Review" or "Sync from Google" to get started.')
                : `No ${activeTab} reviews.`}
            </div>
          </div>
        ) : (
          filteredReviews.map(review => (
            <ReviewCard
              key={review.id}
              review={review}
              onGenerate={handleGenerate}
              onRegenerate={handleRegenerate}
              onApprove={handleApprove}
              onSkip={handleSkip}
              onDelete={(id, name) => setDeleteTarget({ id, name })}
              showTranslation={translateEnabled}
              businessName={business?.name}
              insightsSince={insightsSince}
              isSubscribed={isSubscribed}
            />
          ))
        )}
      </div>

      {showAddModal && <AddReviewModal onClose={() => setShowAddModal(false)} onSubmit={handleAddReview} businessCategory={businessCategory} />}
      {showBulkModal && <BulkImportModal onClose={() => setShowBulkModal(false)} onImported={handleBulkImported} businessCategory={businessCategory} />}
      {deleteTarget && <ConfirmDeleteModal reviewerName={deleteTarget.name} onConfirm={() => handleDelete(deleteTarget.id)} onCancel={() => setDeleteTarget(null)} />}
      {toast && <div className={`toast ${toast.isError ? 'error' : ''}`}>{toast.message}</div>}
    </div>
  );
}
